import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button } from '../ui-v2';
import { Modal } from '../ui-v2.5';
import {
  fetchCamposIncorporaciones, fetchExportarIncorporacionesXLSX,
  fetchCamposBajas, fetchExportarBajasXLSX,
  mensajeDeError, descargarBlob, nombreFichero,
} from '../../../peticiones_API/PeticionesExportacion';
import { filtrosDelEmbudo, filtrosDeBajas } from './api';

/**
 * El botón de exportar el embudo de incorporaciones.
 *
 * ── LO QUE SE DESCARGA ES LO QUE SE ESTÁ VIENDO ───────────────────────────────────────────
 *
 * Los filtros salen de la URL por `filtrosDelEmbudo`, que es **la misma función que usa el
 * listado** para pedir las filas que se están viendo. Una sola traducción, no dos.
 *
 * La primera versión mandaba `location.search` tal cual, con el argumento de que así no había dos
 * listas de nombres que pudieran separarse. Los NOMBRES casaban; **el valor del ámbito, no**: la
 * URL guarda `ambito=colaboradores` y el servidor entiende `colaborador`, así que lo descartaba
 * **sin decir nada**. El botón anunciaba 2 filas y bajaban 11 —el número sale del listado
 * filtrado y la descarga iba por otra vía—.
 *
 * Un parámetro con el nombre bueno y el valor que el servidor descarta se comporta igual que uno
 * que no llegó. Y es invisible porque el nombre es lo que se revisa.
 *
 * `page` no se manda: se exporta lo que el filtro selecciona, no la página visible.
 *
 * ── LOS CAMPOS LOS DICE EL SERVIDOR ───────────────────────────────────────────────────────
 *
 * Esta pantalla NO lleva la lista escrita: la pide. Quien no ve datos personales **no recibe** el
 * correo del candidato —no le llega deshabilitado, no le llega—, así que ni sabe que existe.
 * Esconder una opción es decir que existe; no mandarla, no.
 *
 * Y que exista ese endpoint no relaja nada: el servidor vuelve a validar al exportar, así que
 * pedir a mano un campo que no toca se rechaza con un 400 aunque esta pantalla no lo pinte.
 */
const TITULO_GRUPO = {
  incorporacion: 'La incorporación',
  personales: 'Datos personales',
  baja: 'La baja',
};

/**
 * ── UN BOTÓN, DOS LISTADOS ────────────────────────────────────────────────────────────────
 *
 * El de Bajas pide lo mismo que el del embudo —mismo selector, mismo flujo, mismos errores— y
 * sólo cambian las dos peticiones y la traducción de la URL. Una segunda copia de 150 líneas es
 * cómo acaban divergiendo: la de arriba se arregla y la de abajo no, y nadie se entera porque las
 * dos siguen funcionando.
 *
 * Lo que cambia vive en este mapa y nada más. Añadir un tercer listado es una entrada aquí.
 */
const LISTADOS = {
  incorporaciones: {
    campos: fetchCamposIncorporaciones,
    exportar: fetchExportarIncorporacionesXLSX,
    filtros: filtrosDelEmbudo,
  },
  bajas: {
    campos: fetchCamposBajas,
    exportar: fetchExportarBajasXLSX,
    filtros: filtrosDeBajas,
  },
};

export function BotonExportarEmbudo({ total = 0, listado = 'incorporaciones' }) {
  const cfg = LISTADOS[listado];
  const { search } = useLocation();
  const [abierto, setAbierto] = useState(false);
  const [campos, setCampos] = useState(null);
  const [marcados, setMarcados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [bajando, setBajando] = useState(false);

  const abrir = useCallback(async () => {
    setAbierto(true);
    if (campos) return;
    setCargando(true);
    try {
      const res = await cfg.campos();
      const lista = Array.isArray(res?.campos) ? res.campos : [];
      setCampos(lista);
      // Todo marcado al abrir: el caso normal es «quiero esta tabla», no «quiero elegir».
      setMarcados(lista.filter((c) => c.grupo !== 'personales').map((c) => c.clave));
    } catch (e) {
      toast.error(await mensajeDeError(e, 'No se han podido cargar las columnas.'));
      setAbierto(false);
    } finally {
      setCargando(false);
    }
  }, [campos, cfg]);

  const alternar = (clave) => setMarcados(
    (xs) => (xs.includes(clave) ? xs.filter((x) => x !== clave) : [...xs, clave]),
  );

  const descargar = async () => {
    setBajando(true);
    try {
      const blob = await cfg.exportar(cfg.filtros(search), marcados);
      descargarBlob(blob, nombreFichero(listado));
      setAbierto(false);
    } catch (e) {
      toast.error(await mensajeDeError(e));
    } finally {
      setBajando(false);
    }
  };

  // Los grupos salen de los campos que llegaron, no de una lista escrita aquí: un grupo sin campos
  // —`personales` para quien no los ve— sencillamente no se pinta.
  const grupos = [...new Set((campos || []).map((c) => c.grupo))];

  return (
    <>
      <Button variant="outline" onClick={abrir}>
        <FileSpreadsheet />
        Exportar a Excel
      </Button>

      <Modal
        open={abierto}
        onClose={() => setAbierto(false)}
        title="Exportar a Excel"
        size="lg"
      >
        {cargando ? (
          <div className="flex items-center gap-2 py-6 text-sm text-v25-ink-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Cargando columnas…
          </div>
        ) : (
          <div className="space-y-5">
            <p className="text-sm text-v25-ink-500">
              Se exportan las{' '}
              <strong className="font-medium text-v25-ink-700">{total}</strong>
              {' '}de este filtro, no la página que estás viendo.
            </p>

            {grupos.map((g) => (
              <div key={g}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-v25-ink-400">
                  {TITULO_GRUPO[g] || g}
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
                  {(campos || []).filter((c) => c.grupo === g).map((c) => (
                    <label key={c.clave} className="flex items-center gap-2 text-sm text-v25-ink-700">
                      <input
                        type="checkbox"
                        checked={marcados.includes(c.clave)}
                        onChange={() => alternar(c.clave)}
                        className="h-4 w-4 rounded border-v25-ink-300"
                      />
                      {c.etiqueta}
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setAbierto(false)}>Cancelar</Button>
              {/* Sin ninguna columna no hay fichero que generar: el servidor serviría las básicas
                  —que es lo correcto para el botón que descarga sin preguntar— pero aquí el usuario
                  ha abierto el selector y las ha quitado a propósito, así que decirlo es mejor que
                  darle algo que no pidió. */}
              <Button onClick={descargar} disabled={bajando || marcados.length === 0}>
                {bajando ? <Loader2 className="animate-spin" /> : <FileSpreadsheet />}
                Descargar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

export default BotonExportarEmbudo;
